import {
  hasRole,
  SCORER_TYPES,
  type AgentDto,
  type ProviderDto,
  type Role,
  type ScoreDto,
  type ScorerDto,
  type ScorerType,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Target } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatDateTime, formatPercent } from '@/lib/format';
import { usePaged } from '@/lib/queries';

export const SCORER_TYPE_LABELS: Record<ScorerType, { label: string; hint: string }> = {
  llm_judge: { label: 'LLM judge', hint: 'A model grades each answer against your instructions.' },
  contains: { label: 'Contains text', hint: 'The answer includes (or avoids) a phrase.' },
  regex: { label: 'Matches pattern', hint: 'The answer matches an RE2 regular expression.' },
  json_valid: { label: 'Valid JSON', hint: 'The answer parses as JSON.' },
  length: { label: 'Length', hint: 'The answer’s length is within bounds.' },
  tool_called: { label: 'Tool called', hint: 'The run called a specific tool.' },
};

/** A 0–1 score as a short bar plus percentage (text carries the value; the bar is decoration). */
export function ScoreBar({ score }: { score: number | null }) {
  if (score === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="bg-muted inline-block h-1.5 w-16 overflow-hidden rounded-full" aria-hidden>
        <span
          className="bg-foreground block h-full rounded-full"
          style={{ width: `${score * 100}%` }}
        />
      </span>
      <span className="tabular-nums">{formatPercent(score)}</span>
    </span>
  );
}

function useAgents(tenantId: string) {
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  return agents.data?.pages.flatMap((p) => p.items) ?? [];
}

function NewScorerDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const agents = useAgents(tenantId);
  const providers = usePaged<ProviderDto>(['providers', tenantId], '/providers');
  const providerList = providers.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ScorerType>('llm_judge');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [sampling, setSampling] = useState(100);
  const [instructions, setInstructions] = useState(
    'Is the answer correct, complete and helpful for the user’s question? Penalize made-up facts.',
  );
  const [providerId, setProviderId] = useState('');
  const [model, setModel] = useState('');
  const [text, setText] = useState('');
  const [negate, setNegate] = useState(false);
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const provider = providerList.find((p) => p.id === providerId);

  const config = (): Record<string, unknown> => {
    switch (type) {
      case 'llm_judge':
        return { instructions, scale: { min: 0, max: 10 } };
      case 'contains':
        return { text, negate };
      case 'regex':
        return { pattern: text, negate };
      case 'json_valid':
        return {};
      case 'length':
        return {
          ...(min ? { min: Number(min) } : {}),
          ...(max ? { max: Number(max) } : {}),
        };
      case 'tool_called':
        return { toolName: text };
    }
  };
  const create = useMutation({
    mutationFn: () =>
      api<ScorerDto>('/scorers', {
        method: 'POST',
        body: {
          name,
          description,
          type,
          config: config(),
          agentIds,
          samplingRate: sampling / 100,
          ...(type === 'llm_judge' ? { providerConnectionId: providerId, model } : {}),
        },
      }),
    onSuccess: (s) => {
      toast.success(`Scorer ${s.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['scorers', tenantId] });
      setOpen(false);
    },
  });
  const fieldErrors = create.error instanceof ApiError ? create.error.errors : [];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New scorer</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create a scorer</DialogTitle>
          <DialogDescription>
            Scorers grade finished runs from 0 to 100%. Attached agents are scored automatically.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorAlert error={create.error} />
          {fieldErrors.length > 0 && (
            <ul className="text-destructive list-disc pl-5 text-xs">
              {fieldErrors.map((e) => (
                <li key={e.path}>
                  <span className="font-mono">{e.path}</span>: {e.message}
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sc-name">Name</Label>
              <Input
                id="sc-name"
                className="font-mono"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="helpfulness"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sc-type">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as ScorerType)}>
                <SelectTrigger id="sc-type" aria-label="Scorer type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCORER_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {SCORER_TYPE_LABELS[t].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-muted-foreground -mt-2 text-xs">{SCORER_TYPE_LABELS[type].hint}</p>
          <div className="grid gap-1.5">
            <Label htmlFor="sc-description">Description</Label>
            <Input
              id="sc-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {type === 'llm_judge' && (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="sc-instructions">Grading instructions</Label>
                <Textarea
                  id="sc-instructions"
                  className="min-h-24"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="sc-provider">Judge provider</Label>
                  <Select
                    value={providerId}
                    onValueChange={(v) => {
                      setProviderId(v);
                      setModel(providerList.find((p) => p.id === v)?.defaultModel ?? '');
                    }}
                  >
                    <SelectTrigger id="sc-provider" aria-label="Judge provider">
                      <SelectValue placeholder="Choose a provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providerList.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="sc-model">Judge model</Label>
                  <Input
                    id="sc-model"
                    list="sc-models"
                    className="font-mono"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                  <datalist id="sc-models">
                    {provider?.models.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </div>
              </div>
            </>
          )}
          {(type === 'contains' || type === 'regex' || type === 'tool_called') && (
            <div className="grid gap-1.5">
              <Label htmlFor="sc-text">
                {type === 'contains' ? 'Text' : type === 'regex' ? 'Pattern' : 'Tool name'}
              </Label>
              <Input
                id="sc-text"
                className="font-mono"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              {type !== 'tool_called' && (
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={negate}
                    onChange={(e) => setNegate(e.target.checked)}
                  />
                  Pass when it does <strong>not</strong> {type === 'contains' ? 'contain' : 'match'}
                </label>
              )}
            </div>
          )}
          {type === 'length' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="sc-min">Min characters</Label>
                <Input
                  id="sc-min"
                  type="number"
                  value={min}
                  onChange={(e) => setMin(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="sc-max">Max characters</Label>
                <Input
                  id="sc-max"
                  type="number"
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                />
              </div>
            </div>
          )}

          <fieldset className="grid gap-1">
            <legend className="mb-1 text-sm font-medium">Score these agents automatically</legend>
            {agents.length === 0 && <p className="text-muted-foreground text-sm">No agents yet.</p>}
            {agents.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={agentIds.includes(a.id)}
                  onChange={(e) =>
                    setAgentIds((ids) =>
                      e.target.checked ? [...ids, a.id] : ids.filter((i) => i !== a.id),
                    )
                  }
                />
                {a.name}
              </label>
            ))}
          </fieldset>
          <div className="grid gap-1.5">
            <Label htmlFor="sc-sampling">Sample {sampling}% of runs</Label>
            <input
              id="sc-sampling"
              type="range"
              min={0}
              max={100}
              step={5}
              value={sampling}
              onChange={(e) => setSampling(Number(e.target.value))}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Create scorer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditScorerDialog({ tenantId, scorer }: { tenantId: string; scorer: ScorerDto }) {
  const queryClient = useQueryClient();
  const agents = useAgents(tenantId);
  const [open, setOpen] = useState(false);
  const [agentIds, setAgentIds] = useState(scorer.agentIds);
  const [sampling, setSampling] = useState(Math.round(scorer.samplingRate * 100));
  const [description, setDescription] = useState(scorer.description);
  const save = useMutation({
    mutationFn: () =>
      api<ScorerDto>(`/scorers/${scorer.id}`, {
        method: 'PATCH',
        body: { agentIds, samplingRate: sampling / 100, description },
      }),
    onSuccess: () => {
      toast.success(`Saved ${scorer.name}`);
      void queryClient.invalidateQueries({ queryKey: ['scorers', tenantId] });
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setAgentIds(scorer.agentIds);
          setSampling(Math.round(scorer.samplingRate * 100));
          setDescription(scorer.description);
        } else save.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" aria-label={`Edit ${scorer.name}`}>
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {scorer.name}</DialogTitle>
          <DialogDescription>
            Which agents it scores automatically, and how often.
          </DialogDescription>
        </DialogHeader>
        <ErrorAlert error={save.error} />
        <div className="grid gap-1.5">
          <Label htmlFor="sc-edit-description">Description</Label>
          <Input
            id="sc-edit-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <fieldset className="grid gap-1">
          <legend className="mb-1 text-sm font-medium">Agents</legend>
          {agents.length === 0 && <p className="text-muted-foreground text-sm">No agents yet.</p>}
          {agents.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={agentIds.includes(a.id)}
                onChange={(e) =>
                  setAgentIds((ids) =>
                    e.target.checked ? [...ids, a.id] : ids.filter((i) => i !== a.id),
                  )
                }
              />
              {a.name}
            </label>
          ))}
        </fieldset>
        <div className="grid gap-1.5">
          <Label htmlFor="sc-edit-sampling">Sample {sampling}% of runs</Label>
          <input
            id="sc-edit-sampling"
            type="range"
            min={0}
            max={100}
            step={5}
            value={sampling}
            onChange={(e) => setSampling(Number(e.target.value))}
          />
        </div>
        <DialogFooter>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScorerCards({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const agents = useAgents(tenantId);
  const scorers = useQuery({
    queryKey: ['scorers', tenantId],
    queryFn: () => api<{ items: ScorerDto[] }>('/scorers'),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['scorers', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['scores', tenantId] });
  };
  const toggle = useMutation({
    mutationFn: (s: ScorerDto) =>
      api<ScorerDto>(`/scorers/${s.id}`, { method: 'PATCH', body: { enabled: !s.enabled } }),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const backfill = useMutation({
    mutationFn: (s: ScorerDto) =>
      api<{ scored: number; failed: number }>(`/scorers/${s.id}/backfill`, {
        method: 'POST',
        body: { limit: 20 },
      }),
    onSuccess: (r) => {
      toast.success(`Scored ${r.scored} runs${r.failed ? `, ${r.failed} failed` : ''}`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (s: ScorerDto) => api<void>(`/scorers/${s.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const [deleting, setDeleting] = useState<ScorerDto | null>(null);
  const agentName = (id: string) => agents.find((a) => a.id === id)?.name ?? 'deleted agent';
  const rows = scorers.data?.items ?? [];
  const search = useListSearch(rows, (s) => [
    s.name,
    s.description,
    s.type,
    SCORER_TYPE_LABELS[s.type].label,
  ]);

  if (scorers.error) return <ErrorAlert error={scorers.error} />;
  if (scorers.isPending) return <Skeleton className="h-32" />;
  if (rows.length === 0)
    return (
      <EmptyState title="No scorers yet">
        Create one to grade runs automatically — answer quality with an LLM judge, or checks like
        valid JSON and required phrases.
      </EmptyState>
    );
  return (
    <>
      <ListSearch
        className="mb-4"
        value={search.query}
        onChange={search.setQuery}
        label="Search scorers"
        placeholder="Search by name or type"
      />
      <ul className="grid gap-3 md:grid-cols-2">
        {search.filtered.map((s) => (
          <li key={s.id}>
            <Card className="h-full" data-testid="scorer-card">
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <div className="grid gap-1">
                  <CardTitle className="flex items-center gap-2 font-mono text-base">
                    <Target className="size-4" aria-hidden /> {s.name}
                  </CardTitle>
                  <CardDescription>
                    {s.description || SCORER_TYPE_LABELS[s.type].hint}
                  </CardDescription>
                </div>
                <div className="flex gap-1">
                  <Badge variant="secondary">{SCORER_TYPE_LABELS[s.type].label}</Badge>
                  {!s.enabled && <Badge variant="outline">Paused</Badge>}
                </div>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-muted-foreground text-xs">Average</div>
                    <div className="text-2xl font-semibold tabular-nums">
                      {formatPercent(s.stats.average)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">Scores</div>
                    <div className="text-2xl font-semibold tabular-nums">{s.stats.count}</div>
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">
                  {s.agentIds.length === 0
                    ? 'Manual only (no agents attached)'
                    : `Auto-scores ${Math.round(s.samplingRate * 100)}% of runs of ${s.agentIds.map(agentName).join(', ')}`}
                  {s.model && ` · judge ${s.model}`}
                </p>
              </CardContent>
              {canEdit && (
                <CardFooter className="flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={backfill.isPending || s.agentIds.length === 0}
                    onClick={() => backfill.mutate(s)}
                    title="Score the 20 most recent successful runs of its agents"
                  >
                    {backfill.isPending && backfill.variables?.id === s.id
                      ? 'Scoring…'
                      : 'Score recent runs'}
                  </Button>
                  <EditScorerDialog tenantId={tenantId} scorer={s} />
                  <Button size="sm" variant="ghost" onClick={() => toggle.mutate(s)}>
                    {s.enabled ? 'Pause' : 'Resume'}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => setDeleting(s)}>
                    Delete
                  </Button>
                  <ConfirmDeleteDialog
                    open={deleting?.id === s.id}
                    onOpenChange={(open) => !open && setDeleting(null)}
                    title={`Delete ${s.name}?`}
                    description="The scorer and every score it recorded are deleted. This cannot be undone."
                    onConfirm={() => remove.mutateAsync(s)}
                  />
                </CardFooter>
              )}
            </Card>
          </li>
        ))}
      </ul>
      {search.filtered.length === 0 && <NoMatches query={search.query} />}
    </>
  );
}

function ScoresTable({ tenantId }: { tenantId: string }) {
  const agents = useAgents(tenantId);
  const scorers = useQuery({
    queryKey: ['scorers', tenantId],
    queryFn: () => api<{ items: ScorerDto[] }>('/scorers'),
  });
  const [scorerId, setScorerId] = useState('all');
  const query = scorerId === 'all' ? '' : `?scorerId=${scorerId}`;
  const scores = usePaged<ScoreDto>(['scores', tenantId, query], `/scores${query}`);
  const rows = scores.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card>
      <CardContent className="grid gap-4 pt-6">
        <div className="grid w-56 gap-1.5">
          <Label htmlFor="scores-scorer" className="text-muted-foreground text-xs">
            Scorer
          </Label>
          <Select value={scorerId} onValueChange={setScorerId}>
            <SelectTrigger id="scores-scorer" aria-label="Scorer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scorers</SelectItem>
              {scorers.data?.items.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ErrorAlert error={scores.error} />
        {scores.isPending ? (
          <Skeleton className="h-24" />
        ) : rows.length === 0 ? (
          <EmptyState title="No scores yet">
            Scores appear as attached agents finish runs.
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Scorer</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-xs whitespace-nowrap">
                    <Link
                      to="/traces/runs/$runId"
                      params={{ runId: s.runId }}
                      className="hover:underline"
                    >
                      {formatDateTime(s.createdAt)}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {s.scorerName}
                    {s.source === 'manual' && (
                      <Badge variant="outline" className="ml-1.5">
                        manual
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {agents.find((a) => a.id === s.agentId)?.name ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs">
                    <ScoreBar score={s.score} />
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-md text-xs">
                    {s.reason}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {scores.hasNextPage && (
          <Button
            variant="outline"
            className="justify-self-start"
            onClick={() => scores.fetchNextPage()}
            disabled={scores.isFetchingNextPage}
          >
            Load more
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ScorersContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  return (
    <>
      <PageHeader
        title="Scorers"
        description="Evaluate agent quality: automatic scores on sampled runs, trends in Metrics, and per-run scores in Traces."
        actions={canEdit && <NewScorerDialog tenantId={tenantId} />}
      />
      <Tabs defaultValue="scorers">
        <TabsList>
          <TabsTrigger value="scorers">Scorers</TabsTrigger>
          <TabsTrigger value="scores">Scores</TabsTrigger>
        </TabsList>
        <TabsContent value="scorers">
          <ScorerCards tenantId={tenantId} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="scores">
          <ScoresTable tenantId={tenantId} />
        </TabsContent>
      </Tabs>
    </>
  );
}

export function ScorersPage() {
  return (
    <RequireTenant>
      {(tenant) => <ScorersContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
